import { useEffect, useState } from 'react';
import {
  wallet,
  discovery,
  ApiClientError,
  type ChainInfo,
  type TokenInfo,
} from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime, formatRelative } from '../lib/format';
import { Alert, Badge, Card, CopyButton, Empty, Modal, Spinner, Stat } from '../components/ui';
import { QrButton } from '../components/QrCode';

const BASE_CHAIN_ID = 8453;

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'pending' || status === 'submitted') return 'warning';
  return 'neutral';
}

/**
 * Chain + token picker backed by /discovery (Dextopus catalogue, cached
 * server-side). Falls back to a manual address input when a chain's token
 * list is empty or still loading badly.
 */
function TokenPicker({
  chains,
  chainId,
  onChainChange,
  asset,
  onAssetChange,
  staticOnly,
  excludeChainId,
}: {
  chains: ChainInfo[];
  chainId: number;
  onChainChange: (id: number) => void;
  asset: string;
  onAssetChange: (address: string) => void;
  staticOnly: boolean;
  excludeChainId?: number;
}) {
  const [tokens, setTokens] = useState<TokenInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [manual, setManual] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTokens([]);
    onAssetChange('');

    discovery
      .tokens(chainId, staticOnly)
      .then((list) => {
        if (cancelled) return;
        setTokens(list);
        setManual(list.length === 0);
        // Preselect the obvious stablecoin when there is one.
        const preferred =
          list.find((t) => t.symbol?.toUpperCase() === 'USDC') ??
          list.find((t) => t.symbol?.toUpperCase() === 'USDT') ??
          list[0];
        if (preferred) onAssetChange(preferred.address);
      })
      .catch(() => {
        if (!cancelled) setManual(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, staticOnly]);

  const visibleChains = chains.filter((chain) => chain.chain_id !== excludeChainId);

  return (
    <>
      <label className="field">
        <span>Chain</span>
        <select value={chainId} onChange={(event) => onChainChange(Number(event.target.value))}>
          {visibleChains.map((chain) => (
            <option key={chain.chain_id} value={chain.chain_id}>
              {chain.name}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>Token</span>
        {manual ? (
          <input
            value={asset}
            onChange={(event) => onAssetChange(event.target.value)}
            placeholder="Token address (0x… / base58…)"
            className="mono"
          />
        ) : (
          <select
            value={asset}
            onChange={(event) => {
              if (event.target.value === '__manual__') {
                setManual(true);
                onAssetChange('');
              } else {
                onAssetChange(event.target.value);
              }
            }}
            disabled={loading}
          >
            {loading && <option>Loading tokens…</option>}
            {tokens.map((token) => (
              <option key={token.address} value={token.address}>
                {token.symbol} — {token.name}
              </option>
            ))}
            <option value="__manual__">Other (enter address)…</option>
          </select>
        )}
      </label>
    </>
  );
}

export function Wallet() {
  const info = useResource(
    () =>
      wallet
        .get()
        .catch((e) =>
          e instanceof ApiClientError && e.status === 404 ? null : Promise.reject(e)
        ),
    [],
    { refreshMs: 25000 }
  );
  const addresses = useResource(() => wallet.depositAddresses().catch(() => []), []);
  const deposits = useResource(() => wallet.deposits(1).catch(() => null), [], { refreshMs: 25000 });
  const transfers = useResource(() => wallet.transfers(1).catch(() => null), [], { refreshMs: 25000 });
  const chains = useResource(() => discovery.chains().catch(() => []), []);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [addingAddress, setAddingAddress] = useState(false);
  const [depositChainId, setDepositChainId] = useState(1);
  const [depositAsset, setDepositAsset] = useState('');

  const [sending, setSending] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [sendChainId, setSendChainId] = useState(BASE_CHAIN_ID);
  const [sendAsset, setSendAsset] = useState('');

  const chainName = (id: number) =>
    chains.data?.find((chain) => chain.chain_id === id)?.name ?? String(id);

  async function run(fn: () => Promise<unknown>, after?: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      if (after) await after();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  }

  if (info.loading) return <Spinner />;

  if (!info.data) {
    return (
      <>
        <div className="page-header">
          <h1>Wallet</h1>
          <p>A smart account on Base that holds your USDC inventory.</p>
        </div>
        {error && <Alert>{error}</Alert>}
        <Card>
          <Empty>
            <p>No wallet yet.</p>
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => run(() => wallet.create(), () => info.reload())}
            >
              {busy ? 'Creating…' : 'Create wallet'}
            </button>
          </Empty>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <h1>Wallet</h1>
        <p>USDC on Base — deposits from any chain land here.</p>
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="stat-grid">
        <Stat label="Balance (USDC)" value={info.data.balance} />
        <Stat
          label="Smart account"
          value={
            <span className="mono" style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {info.data.address.slice(0, 10)}…{info.data.address.slice(-6)}
              <CopyButton value={info.data.address} />
              <QrButton
                value={info.data.address}
                title="Smart account"
                subtitle="USDC on Base only — deposits on other chains need a deposit address."
              />
            </span>
          }
        />
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        <Card
          title="Top-up deposit addresses"
          actions={
            <button className="btn btn-primary btn-sm" onClick={() => setAddingAddress(true)}>
              New address
            </button>
          }
        >
          {!addresses.data?.length ? (
            <Empty>Create an address for any chain/token — it settles here as USDC.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Chain</th>
                    <th>Token</th>
                    <th>Deposit address</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {addresses.data.map((address) => (
                    <tr key={address.id}>
                      <td>{chainName(address.origin_chain_id)}</td>
                      <td className="mono">{address.origin_asset.slice(0, 14)}…</td>
                      <td className="mono">{address.deposit_address}</td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <CopyButton value={address.deposit_address} />
                        <QrButton
                          value={address.deposit_address}
                          title={`Deposit on ${chainName(address.origin_chain_id)}`}
                          subtitle="Send only the listed token on this chain — it settles to your wallet as USDC on Base."
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Send crypto"
          actions={
            <button className="btn btn-primary btn-sm" onClick={() => setSending(true)}>
              Send
            </button>
          }
        >
          {!transfers.data?.items.length ? (
            <Empty>No outbound transfers yet.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>To</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Tx</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.data.items.map((transfer) => (
                    <tr key={transfer.id}>
                      <td className="muted">{formatRelative(transfer.created_at)}</td>
                      <td className="mono">
                        {transfer.destination_address.slice(0, 10)}…
                        {transfer.destination_address.slice(-4)}
                      </td>
                      <td className="mono">{transfer.amount}</td>
                      <td>{transfer.method}</td>
                      <td>
                        <Badge tone={statusTone(transfer.status)}>{transfer.status}</Badge>
                      </td>
                      <td className="mono muted">
                        {transfer.tx_hash ? `${transfer.tx_hash.slice(0, 10)}…` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Deposits">
          {!deposits.data?.items.length ? (
            <Empty>No deposits yet.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Origin</th>
                    <th>Received (USDC)</th>
                    <th>Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.data.items.map((deposit) => (
                    <tr key={deposit.id}>
                      <td className="muted">{formatDateTime(deposit.created_at)}</td>
                      <td>{chainName(deposit.origin_chain_id)}</td>
                      <td className="mono">{deposit.amount ?? '—'}</td>
                      <td>
                        {deposit.offramp_order_id ? (
                          <Badge tone="accent">offramp</Badge>
                        ) : (
                          'top-up'
                        )}
                      </td>
                      <td>
                        <Badge tone={statusTone(deposit.status)}>{deposit.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {addingAddress && (
        <Modal
          title="New deposit address"
          onClose={() => setAddingAddress(false)}
          footer={
            <>
              <button className="btn" onClick={() => setAddingAddress(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={busy || !depositAsset}
                onClick={() =>
                  run(
                    () => wallet.createDepositAddress(depositChainId, depositAsset),
                    async () => {
                      setAddingAddress(false);
                      await addresses.reload();
                    }
                  )
                }
              >
                {busy ? 'Creating…' : 'Create'}
              </button>
            </>
          }
        >
          <TokenPicker
            chains={chains.data ?? []}
            chainId={depositChainId}
            onChainChange={setDepositChainId}
            asset={depositAsset}
            onAssetChange={setDepositAsset}
            staticOnly
          />
          <p className="muted" style={{ fontSize: 12.5, marginBottom: 0 }}>
            Anything sent to the generated address arrives in your wallet as USDC on Base.
          </p>
        </Modal>
      )}

      {sending && (
        <Modal
          title="Send crypto"
          onClose={() => setSending(false)}
          footer={
            <>
              <button className="btn" onClick={() => setSending(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={
                  busy || !sendTo || !sendAmount || (sendChainId !== BASE_CHAIN_ID && !sendAsset)
                }
                onClick={() =>
                  run(
                    () =>
                      wallet.transfer({
                        destination_address: sendTo,
                        amount: sendAmount,
                        ...(sendChainId !== BASE_CHAIN_ID && {
                          destination_chain_id: sendChainId,
                          destination_asset: sendAsset,
                        }),
                      }),
                    async () => {
                      setSending(false);
                      setSendTo('');
                      setSendAmount('');
                      setSendChainId(BASE_CHAIN_ID);
                      setSendAsset('');
                      await Promise.all([info.reload(), transfers.reload()]);
                    }
                  )
                }
              >
                {busy ? 'Sending…' : 'Send'}
              </button>
            </>
          }
        >
          <label className="field">
            <span>Destination chain</span>
            <select
              value={sendChainId}
              onChange={(event) => setSendChainId(Number(event.target.value))}
            >
              {(chains.data ?? []).map((chain) => (
                <option key={chain.chain_id} value={chain.chain_id}>
                  {chain.name}
                  {chain.chain_id === BASE_CHAIN_ID ? ' (direct USDC)' : ''}
                </option>
              ))}
            </select>
            {sendChainId !== BASE_CHAIN_ID && (
              <span className="muted" style={{ fontSize: 12.5 }}>
                Cross-chain: your USDC converts and delivers as the chosen token.
              </span>
            )}
          </label>

          {sendChainId !== BASE_CHAIN_ID && (
            <TokenPicker
              chains={chains.data ?? []}
              chainId={sendChainId}
              onChainChange={setSendChainId}
              asset={sendAsset}
              onAssetChange={setSendAsset}
              staticOnly={false}
              excludeChainId={-1}
            />
          )}

          <label className="field">
            <span>Destination address</span>
            <input
              value={sendTo}
              onChange={(event) => setSendTo(event.target.value)}
              className="mono"
              placeholder="0x… / base58…"
            />
          </label>
          <label className="field">
            <span>Amount to debit (USDC)</span>
            <input
              value={sendAmount}
              onChange={(event) => setSendAmount(event.target.value)}
              placeholder="100.00"
            />
          </label>
        </Modal>
      )}
    </>
  );
}
